/**
 * Notification API Layer
 * Handles GraphQL queries and REST endpoints for notifications
 */

import { graphqlQuery } from '@/services/graphql'
import apiClient from '@/api/client'
import type {
  MyNotificationsResponse,
  NotificationQueryOptions,
  DismissAllOptions,
  FCMDeviceRegistration,
  Notification,
} from '@/types/notifications'
import { transformNotification } from '@/types/notifications'

// GraphQL Query for fetching notifications
const MY_NOTIFICATIONS_QUERY = `
  query MyNotifications($limit: Int!, $offset: Int!, $isRead: Boolean) {
    myNotifications(limit: $limit, offset: $offset, isRead: $isRead) {
      totalCount
      logs {
        id
        title
        body
        channel
        status
        isRead
        readAt
        timestamp
      }
    }
  }
`

/**
 * Fetch notifications using GraphQL
 * Uses the new backend myNotifications query
 */
export async function fetchNotifications(
  options: NotificationQueryOptions = {}
): Promise<{
  notifications: Notification[]
  totalCount: number
  unreadCount?: number
}> {
  const { limit = 20, offset = 0, isRead } = options

  try {
    const response = await graphqlQuery<MyNotificationsResponse>(
      MY_NOTIFICATIONS_QUERY,
      {
        limit,
        offset,
        isRead,
      }
    )

    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors.map(e => e.message).join('; '))
    }

    const data = response.data?.myNotifications
    
    if (!data) {
      return {
        notifications: [],
        totalCount: 0,
      }
    }

    const notifications = data.logs.map(transformNotification)

    return {
      notifications,
      totalCount: data.totalCount,
    }
  } catch (error) {
    console.error('[Notifications API] Failed to fetch notifications:', error)
    throw error
  }
}

/**
 * Fetch unread notifications count
 * Useful for badge display without fetching all notifications
 */
export async function fetchUnreadCount(): Promise<number> {
  try {
    const response = await graphqlQuery<MyNotificationsResponse>(
      MY_NOTIFICATIONS_QUERY,
      {
        limit: 1,
        offset: 0,
        isRead: false,
      }
    )

    return response.data?.myNotifications?.totalCount ?? 0
  } catch (error) {
    console.error('[Notifications API] Failed to fetch unread count:', error)
    return 0
  }
}

/**
 * Mark a single notification as read
 * REST endpoint: POST /api/v1/communications/notifications/<id>/dismiss/
 */
export async function dismissNotification(id: number): Promise<void> {
  try {
    await apiClient.post(`/api/v1/communications/notifications/${id}/dismiss/`)
  } catch (error) {
    console.error('[Notifications API] Failed to dismiss notification:', error)
    throw error
  }
}

/**
 * Mark all notifications as read
 * REST endpoint: POST /api/v1/communications/notifications/dismiss-all/
 * 
 * @param options - Optional filter by profile_type and/or channel
 */
export async function dismissAllNotifications(
  options?: DismissAllOptions
): Promise<void> {
  try {
    await apiClient.post(
      '/api/v1/communications/notifications/dismiss-all/',
      options || {}
    )
  } catch (error) {
    console.error('[Notifications API] Failed to dismiss all notifications:', error)
    throw error
  }
}

/**
 * Register FCM device token for push notifications
 * REST endpoint: POST /api/v1/communications/devices/
 */
export async function registerFCMDevice(
  registration: FCMDeviceRegistration
): Promise<void> {
  try {
    await apiClient.post('/api/v1/communications/devices/', registration)
  } catch (error) {
    console.error('[Notifications API] Failed to register FCM device:', error)
    throw error
  }
}

/**
 * Unregister FCM device token
 * REST endpoint: DELETE /api/v1/communications/devices/<registration_id>/
 */
export async function unregisterFCMDevice(registrationId: string): Promise<void> {
  try {
    await apiClient.delete(`/api/v1/communications/devices/${registrationId}/`)
  } catch (error) {
    console.error('[Notifications API] Failed to unregister FCM device:', error)
    throw error
  }
}
